/* eslint-disable max-statements */

import { Button, Checkbox, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { LeavePageNotification, NumberInput } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';

const risksCalculationSchema = z.object({
  riskAnalysisEnabled: z.boolean().nullable().optional(),
  amazonFeesCalculationEnabled: z.boolean().nullable().optional(),
  screenshotsEnabled: z.boolean().nullable().optional(),
  aiModuleEnabled: z.boolean().nullable().optional(),
  penaltyDescription: z.number().nullable().optional(),
  penaltyValue: z.number().nullable().optional(),
  penaltyHSCode: z.number().nullable().optional(),
  requestProofEnabled: z.boolean().nullable().optional(),
  vatValidationEnabled: z.boolean().nullable().optional(),
  eoriValidationEnabled: z.boolean().nullable().optional(),
});

export const RisksCalculationForm = () => {
  const { data: declarant, isLoading } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();
  const { data: settings } = useSettings();

  const buildInitialValues = (dec) => ({
    risksCalculation: {
      riskAnalysisEnabled: dec?.risksCalculation?.riskAnalysisEnabled || false,
      amazonFeesCalculationEnabled: dec?.risksCalculation?.amazonFeesCalculationEnabled || false,
      screenshotsEnabled: dec?.risksCalculation?.screenshotsEnabled || false,
      aiModuleEnabled: dec?.risksCalculation?.aiModuleEnabled || false,
      penaltyDescription: dec?.risksCalculation?.penaltyDescription || 0,
      penaltyValue: dec?.risksCalculation?.penaltyValue || 0,
      penaltyHSCode: dec?.risksCalculation?.penaltyHSCode || 0,
      requestProofEnabled: dec?.risksCalculation?.requestProofEnabled || false,
      vatValidationEnabled: dec?.risksCalculation?.vatValidationEnabled || false,
      eoriValidationEnabled: dec?.risksCalculation?.eoriValidationEnabled || false,
    },
  });

  const onSubmit = (values: any) => {
    updateDeclarantMutation.mutate({
      declarantId: declarant._id,
      declarant: values,
    });
  };

  const form = useFormWithNameInputProp({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(risksCalculationSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({} as any);
  }, [declarant]);

  const displayCustomsProof = settings?.walletEnabled && declarant.virtuals?.hasWallet;

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="xl" maw={450}>
          <Stack gap="md" mb="xl">
            <Title order={4} fw={600}>
              Web method value, description & snapshot
            </Title>

            <Stack gap="xs">
              <Text>If you enable Risk analysis, you will run the risk analysis for your declarations, enhanced with the web values and web descriptions.</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.riskAnalysisEnabled')}
                label="Risk analysis enabled"
                checked={form.values.risksCalculation.riskAnalysisEnabled}
              />
            </Stack>

            <Stack gap="xs">
              <Text>If you enable amazon fees calculations, you will run the amazon calculator for each declarations with an amazon web url.</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.amazonFeesCalculationEnabled')}
                label="Amazon Fees Calculation"
                checked={form.values.risksCalculation.amazonFeesCalculationEnabled}
              />
            </Stack>

            <Stack gap="xs">
              <Text>If you enable screenshots, you will take screenshots for each of the goods items in your declarations.</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.screenshotsEnabled')}
                label="Screenshots enabled"
                checked={form.values.risksCalculation.screenshotsEnabled}
              />
            </Stack>

            <Stack gap="xs">
              <Text>If you enable AI module, you will run the HS code analysis for each of the goods items in your declarations.</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.aiModuleEnabled')}
                label="AI module enabled"
                checked={form.values.risksCalculation.aiModuleEnabled}
              />
            </Stack>
          </Stack>

          <Stack gap="md" mb="xl">
            <Title order={4} fw={600}>
              VAT and EORI validation
            </Title>

            <Stack gap="xs">
              <Text>If you enable VAT verification, you will run the VIES VAT number validation before processing the batch upload or batch update</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.vatValidationEnabled')}
                label="VAT verification enabled"
                checked={form.values.risksCalculation.vatValidationEnabled}
              />
            </Stack>

            <Stack gap="xs">
              <Text>If you enable EORI verification, you will run the EORI number validation before processing the batch upload or batch update</Text>
              <Checkbox
                {...form.getInputProps('risksCalculation.eoriValidationEnabled')}
                label="EORI verification enabled"
                checked={form.values.risksCalculation.eoriValidationEnabled}
              />
            </Stack>
          </Stack>

          {displayCustomsProof && (
            <Stack gap="md" mb="xl">
              <Title order={4} fw={600}>
                Customs Proof
              </Title>

              <Checkbox
                {...form.getInputProps('risksCalculation.requestProofEnabled')}
                label="Request proof (only low values)"
                checked={form.values.risksCalculation.requestProofEnabled}
              />
            </Stack>
          )}

          <Stack gap="md" mb="xl">
            <Title order={4} fw={600}>
              Penalty value
            </Title>

            <Stack gap="xs">
              <Text>Amount in € to pay when the value is underestimated</Text>
              <NumberInput {...form.getInputProps('risksCalculation.penaltyValue')} label="Penalty value" />
            </Stack>
          </Stack>

          <Stack gap="md" mb="xl">
            <Title order={4} fw={600}>
              Penalty description
            </Title>

            <Stack gap="xs">
              <Text>Amount in € to pay when the description is not matching with the commercial description on web</Text>
              <NumberInput {...form.getInputProps('risksCalculation.penaltyDescription')} label="Penalty description" />
            </Stack>
          </Stack>

          <Stack gap="md" mb="xl">
            <Title order={4} fw={600}>
              Penalty HS code
            </Title>

            <Stack gap="xs">
              <Text>Amount in € to pay when the HS code is not matching with the commercial description</Text>
              <NumberInput {...form.getInputProps('risksCalculation.penaltyHSCode')} label="Penalty HS code" />
            </Stack>
          </Stack>

          <Group py="xl">
            <Button type="submit" variant="filled" size="lg">
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};
